import { useContext, useEffect, useMemo, useState } from 'react';
import { get } from 'lodash';
import { Icon } from '@iconify/react';
import styled from '@emotion/styled';
import { Box, Grid, MenuItem, Skeleton, Typography, lighten } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppSelector } from '@/redux/hooks';
import { selectBaseLanguages } from '@/redux/selectors/constantsSelectors';
import { isActiveVersionAVersion } from '@/redux/slices/slice_types';
import { selectRootTitle, selectActiveTitle, selectActiveVersion, selectIsRootTitleOrVersionsLoading } from '@/redux/selectors/titleV2Selectors';
import axios from '@/_utils/axios';
import { RJSFSchema } from '@rjsf/utils';
import { LabelValue, Spinner } from '@/components/_common';
import SchemaForm from '@/components/_common/SchemaForm/SchemaForm';
import DropdownSelect from '@/components/_common/Form/DropdownSelect';
import CSVLanguages from '@/components/_common/CSVLanguages/CSVLanguages';
import SizePreservingLoader from '@/components/_common/SizePreservingLoader/SizePreservingLoader';

interface TitleMetadata {
  metadata_values: Record<string, unknown>;
  metadata_schema: RJSFSchema;
}

/**
 * Returns the most prioritized language from the given list of languages
 * based on a priority list. If none of the priority languages are found,
 * it returns the first language from the provided list.
 *
 * @param {string[]} languages - An array of available languages.
 * @param {string[]} langsPriority - An array of prioritized languages in order of preference.
 * @returns {string} - The most prioritized language, or the first language from the input list if no priority match is found.
 */
const getPriorityLanguage = (languages: string[], langsPriority: string[]) => {
  if (languages.find((l) => typeof l !== 'string')) return '';
  const langSet = new Set(Array.from(languages).map((lang) => lang.replace(/-/g, '_')));
  const lang = langsPriority.find((language) => langSet.has(language)) || languages[0];
  return languages[0].indexOf('-') > -1 ? lang.replace(/_/g, '-') : lang;
};

const createBUGroupings = (BULang?: { language: string; hierarchy: string }[] | string[]) => {
  if (!BULang || BULang.length === 0 || BULang.some((l) => typeof l === 'string' || !l)) return undefined;
  const grouped = BULang.reduce((acc: Record<string, string[]>, curr) => {
    if (!curr || !curr.hierarchy) return acc;
    if (!acc[curr.hierarchy]) {
      acc[curr.hierarchy] = [];
    }
    acc[curr.hierarchy].push(curr.language);
    return acc;
  }, {});
  return Object.entries(grouped).length ? grouped : undefined;
};

const TitleDetailMetadataV2 = ({ disableDropdownOffset = false }: { disableDropdownOffset?: boolean }) => {
  const rootTitle = useAppSelector(selectRootTitle);
  const activeTitle = useAppSelector(selectActiveTitle);
  const customFields = useContext(ConfigContext)?.titleConfig?.metadata;
  const isLoading = useAppSelector(selectIsRootTitleOrVersionsLoading);
  const activeVersion = useAppSelector(selectActiveVersion);
  const baseLanguages = useAppSelector(selectBaseLanguages);
  const languagesPriority = useMemo(() => (baseLanguages ? baseLanguages.map((lang) => lang.code) : []), [baseLanguages]);
  const [fetchingTitleMetadata, setFetchingTitleMetadata] = useState(false);
  const [titleMetadata, setTitleMetadata] = useState<TitleMetadata | null>(null);
  const [versionMetadata, setVersionMetadata] = useState<TitleMetadata | null>(null);
  const titleMetadataLanguages = activeTitle?.metadata_languages;
  const [selectedLanguage, setSelectedLanguage] = useState(titleMetadataLanguages?.[0] || '');
  const BULanguageList = useMemo(
    () => createBUGroupings(activeVersion?.metadata_languages || activeTitle?.metadata_languages),
    [activeVersion, activeTitle]
  );
  const [selectedBU, setSelectedBU] = useState<undefined | string>(Object.keys(BULanguageList || {})[0]);

  const fetchMetadata = (titleId: string | number, language: string, BU?: string): Promise<TitleMetadata | null> => {
    setFetchingTitleMetadata(true);
    return axios
      .get(`/inventory/titles/${titleId}/metadata/${language}/${BU ? `?hierarchy=${BU}` : ''}`)
      .then((res) => {
        if (!res.data) {
          throw new Error('No metadata found');
        } else {
          return res.data?.data as TitleMetadata;
        }
      })
      .catch((error) => {
        console.error(error);
        return null;
      })
      .finally(() => {
        setFetchingTitleMetadata(false);
      });
  };

  useEffect(() => {
    setTitleMetadata(null);
    if (titleMetadataLanguages && (titleMetadataLanguages?.length ?? 0 > 0)) {
      if (!BULanguageList) {
        const language = getPriorityLanguage(titleMetadataLanguages, languagesPriority);
        setSelectedLanguage(language);
        fetchMetadata(activeTitle.id, language).then((metadata) => {
          setTitleMetadata(metadata);
        });
      } else {
        const BU = Object.keys(BULanguageList || {})[0];
        const language = getPriorityLanguage(BULanguageList[BU], languagesPriority);
        if (!language || language.length === 0) return;
        setSelectedLanguage(language);
        setSelectedBU(BU);
        fetchMetadata(activeTitle.id, language, BU).then((metadata) => {
          setTitleMetadata(metadata);
        });
      }
    }
  }, [activeTitle]);

  useEffect(() => {
    setVersionMetadata(null);
    if (isActiveVersionAVersion(activeVersion) && activeVersion.metadata_languages && activeVersion.metadata_languages.length > 0) {
      if (!BULanguageList) {
        const language = getPriorityLanguage(activeVersion.metadata_languages, languagesPriority);
        setSelectedLanguage(language);
        fetchMetadata(activeVersion.id, language).then((metadata) => {
          setVersionMetadata(metadata);
        });
      } else {
        const BU = Object.keys(BULanguageList || {})[0];
        const language = getPriorityLanguage(BULanguageList[BU], languagesPriority);
        if (!language || language.length === 0) return;
        setSelectedLanguage(language);
        setSelectedBU(BU);
        fetchMetadata(activeVersion.id, language, BU).then((metadata) => {
          setVersionMetadata(metadata);
        });
      }
    }
  }, [activeVersion]);

  const selectLanguage = (language: string, bu?: string) => {
    setSelectedLanguage(language);
    if (bu) setSelectedBU(bu);
    if (isActiveVersionAVersion(activeVersion)) {
      fetchMetadata(activeVersion.id, language, bu || selectedBU).then((metadata) => {
        setVersionMetadata(metadata);
      });
    }
    if (activeTitle) {
      fetchMetadata(activeTitle.id, language, bu || selectedBU).then((metadata) => {
        setTitleMetadata(metadata);
      });
    }
  };

  const languageOptions =
    (selectedBU && BULanguageList && BULanguageList[selectedBU]) ||
    (isActiveVersionAVersion(activeVersion) && activeVersion.metadata_languages) ||
    titleMetadataLanguages ||
    [];
  return (
    <div style={{ position: 'relative' }}>
      {fetchingTitleMetadata && <Spinner />}
      {/* {selectedBU && BULanguageList && BULanguageList[selectedBU] && (
        <LanguageDropdown style={{ top: disableDropdownOffset ? -78 : -70, right: disableDropdownOffset ? 396 : 220 }}>
          <DropdownSelect
            label="Business Unit"
            value={selectedBU}
            onChange={(b) => selectLanguage(BULanguageList[b], b)}
            disabled={Object.keys(BULanguageList || {}).length === 1}
          >
            {Object.keys(BULanguageList || {})?.map((bu: string) => (
              <MenuItem key={bu} value={bu} sx={{ display: 'flex', alignItems: 'center', fontSize: '1.2rem' }}>
                {bu}
              </MenuItem>
            ))}
          </DropdownSelect>
        </LanguageDropdown>
      )} */}
      {languageOptions?.length > 0 && (
        <LanguageDropdown style={{ top: disableDropdownOffset ? -78 : -70, right: disableDropdownOffset ? 176 : 0 }}>
          <DropdownSelect label="Language" value={selectedLanguage} onChange={selectLanguage} disabled={languageOptions?.length === 1}>
            {languageOptions?.map((lang: string) => (
              <MenuItem key={lang} value={lang} sx={{ display: 'flex', alignItems: 'center', fontSize: '1.2rem' }}>
                {/* <Box sx={{ fontSize: '1.6rem', mr: 1 }}>{FLAG_ICONS[lang.toLowerCase() as keyof typeof FLAG_ICONS]}</Box> */}
                <CSVLanguages codes={[lang]} type="base" />
              </MenuItem>
            ))}
          </DropdownSelect>
        </LanguageDropdown>
      )}
      <SizePreservingLoader isLoading={isLoading} loader={<SkeletonLoader />}>
        <Grid container columnSpacing={6} rowSpacing={3} sx={{ position: 'relative', opacity: fetchingTitleMetadata ? 0.2 : 1 }}>
          {titleMetadata ? (
            <Grid item sx={{ mt: disableDropdownOffset ? 4 : 0 }}>
              <SchemaForm schema={titleMetadata?.metadata_schema} formData={titleMetadata?.metadata_values} readonly />
            </Grid>
          ) : (
            //  CUSTOM CLIENT FIELDS FROM CONFIG
            !!customFields &&
            Object.keys(customFields).map((field) => {
              const shouldDisplayForType = !customFields[field].entity_type || customFields[field].entity_type?.includes(activeTitle?.entity_type);
              if (!shouldDisplayForType) return null;
              const fieldLocation = field.split('__').join('.');
              const value = get(activeTitle, fieldLocation);
              let label = customFields[field].label;
              label = label.replace(/{{(.+?)}}/g, activeTitle?.entity_type);
              label = label.substring(0, 1).toUpperCase() + label.substring(1);
              return (
                <LabelValue key={field} label={label}>
                  {customFields[field].render ? customFields[field].render(value, activeTitle, rootTitle) : value}
                </LabelValue>
              );
            })
          )}
        </Grid>
      </SizePreservingLoader>
      {isActiveVersionAVersion(activeVersion) && versionMetadata && (
        <VersionBox>
          <Typography variant="h6" color="primary" sx={{ fontSize: '1.2rem', mb: 3, display: 'flex' }}>
            <Icon height={26} icon="iconamoon:arrow-bottom-right-3-square-thin" style={{ marginRight: 5 }} />
            <span>{activeVersion.name}</span>
          </Typography>
          <SchemaForm schema={versionMetadata.metadata_schema} formData={versionMetadata.metadata_values} readonly />
        </VersionBox>
      )}
    </div>
  );
};

export default TitleDetailMetadataV2;

const SkeletonLoader = () => (
  <Grid container direction="column" rowSpacing={3} justifyContent="space-around">
    <Grid item>
      <Skeleton
        sx={{
          transform: 'unset' // by default skeleton is squeezed vertically (has scale(1, .6)), so by applying `transform: 'unset'` we cancel that default transformation.
        }}
      />
    </Grid>
    <Grid item>
      <Skeleton sx={{ transform: 'unset' }} />
    </Grid>
    <Grid item>
      <Skeleton sx={{ transform: 'unset' }} />
    </Grid>
    <Grid item>
      <Skeleton sx={{ transform: 'unset' }} />
    </Grid>
    <Grid item>
      <Skeleton sx={{ transform: 'unset' }} />
    </Grid>
  </Grid>
);

const VersionBox = styled(Box)`
  margin-top: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => lighten(theme.palette.background.default, 0.03)};
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;

const LanguageDropdown = styled(Box)`
  display: inline-flex;
  align-items: center;
  position: absolute;
  z-index: 1;
  right: 0;

  .MuiFormControl-root {
    min-width: 200px;
  }

  .MuiSelect-select {
    display: flex;
    align-items: center;
    font-size: 1.2rem;
  }
`;
